import { RouterProvider, createBrowserRouter, Navigate } from "react-router-dom";
import { Suspense, lazy } from "react";

import { useAuth } from "../provider/AuthProvider";
import { useInstalledApps } from "../provider/InstalledAppsProvider";
import { ProtectedRoute } from "./ProtectedRoute";
import RegistryInitializer from "../components/RegistryInitializer";
import AIChatOverlay from "../components/AIChat/AIChatOverlay";

// Lazy Load Pages
const Homepage = lazy(() => import("../pages/components/Homepage"));
const AIHome = lazy(() => import("../pages/home/AIHome"));
const Login = lazy(() => import("../pages/Login"));
const Register = lazy(() => import("../pages/Register"));
const Logout = lazy(() => import("../pages/Logout"));
const Profile = lazy(() => import("../pages/Profile"));
const Dashboard = lazy(() => import("../pages/dashboard/Dashboard"));
const SaaSAdmin = lazy(() => import("../pages/SaaSAdmin"));

// Helpdesk
const TicketsCreate = lazy(() => import("../pages/helpdesk/TicketCreate"));
const TicketsGrid = lazy(() => import("../pages/helpdesk/TicketsGrid"));
const TicketsList = lazy(() => import("../pages/helpdesk/TicketsList"));
const TicketsForm = lazy(() => import("../pages/helpdesk/TicketsViewEdit"));
const TicketDetails = lazy(() => import("../pages/helpdesk/TicketDetailsLayout/TicketDetails"));
const HelpdeskDashboard = lazy(() => import("../pages/helpdesk/HelpdeskDashboard"));
const HelpdeskReports = lazy(() => import("../pages/helpdesk/HelpdeskReports"));
const HelpdeskLayout = lazy(() => import("../pages/helpdesk/HelpdeskLayout"));

// Contacts & Users
const ContactList = lazy(() => import("../pages/contacts/ContactList"));
const ContactsGrid = lazy(() => import("../pages/contacts/ContactGrid"));
const ContactCreate = lazy(() => import("../pages/contacts/ContactCreate"));
const ContactEdit = lazy(() => import("../pages/contacts/ContactEdit"));
const ContactDetails = lazy(() => import("../pages/contacts/ContactDetailsLayout/ContactDetails"));
const ContactListMain = lazy(() => import("../pages/contacts/ContactListMain"));
const UsersList = lazy(() => import("../pages/users/UsersList"));
const UsersGrid = lazy(() => import("../pages/users/UsersGrid"));
const UserCreate = lazy(() => import("../pages/users/UserCreate"));
const UserDetails = lazy(() => import("../pages/users/UserDetailsLayout/UserDetails"));
const UserEdit = lazy(() => import("../pages/users/UserEdit"));
const SLACreate = lazy(() => import("../pages/helpdesk/SLACreate"));
// Settings
const Settings = lazy(() => import("../pages/settings/Settings"));
const SettingsEdit = lazy(() => import("../pages/settings/SettingsEdit"));
const AISettings = lazy(() => import("../pages/settings/AISettings"));
const GatewaySettings = lazy(() => import("../pages/settings/GatewaySettings"));
const ResourceCalendarList = lazy(() => import("../pages/settings/ResourceCalendarList"));
const ResourceCalendarDetails = lazy(() => import("../pages/settings/ResourceCalendarDetails"));
const TranslationsSettings = lazy(() => import("../pages/settings/TranslationsSettings"));
const EmailConfiguration = lazy(() => import("../pages/email/EmailConfiguration"));
const EmailLogs = lazy(() => import("../pages/email/EmailLogs"));

// Company
const CompanyCreate = lazy(() => import("../pages/company/CompanyCreate"));
const CompanyDetails = lazy(() => import("../pages/company/CompanyDetailsLayout/CompanyDetails"));
const CompanyEdit = lazy(() => import("../pages/company/CompanyEdit"));
const CompanyList = lazy(() => import("../pages/company/CompanyList"));
const CompanyGrid = lazy(() => import("../pages/company/CompanyGrid"));

// Marketing
const Marketing = lazy(() => import("../pages/marketing/Marketing"));
const MarketingDashboard = lazy(() => import("../pages/marketing/MarketingDashboard"));
const CampaignsList = lazy(() => import("../pages/marketing/CampaignsList"));
const CampaignAnalytics = lazy(() => import("../pages/marketing/CampaignAnalytics"));
const ContactsAudience = lazy(() => import("../pages/marketing/ContactsAudience"));
const FlowBuilder = lazy(() => import("../pages/marketing/CampaignBuilder/FlowBuilder"));
const TemplatesList = lazy(() => import("../pages/email/TemplatesList"));
const MarketingTemplatesList = lazy(() => import("../pages/marketing/MarketingTemplatesList"));
const LoyaltyDashboard = lazy(() => import("../pages/marketing/LoyaltyDashboard"));

// Knowledge System
const KnowledgeBaseLayout = lazy(() => import("../pages/knowledge_system/layout/KnowledgeBaseLayout"));
const KnowledgeDashboard = lazy(() => import("../pages/knowledge_system/Dashboard"));
const KnowledgeHome = lazy(() => import("../pages/knowledge_system/KnowledgeHome"));
const ArticleList = lazy(() => import("../pages/knowledge_system/ArticleList"));
const ArticleDetail = lazy(() => import("../pages/knowledge_system/ArticleDetailsLayout/ArticleDetails"));
const ArticleCreate = lazy(() => import("../pages/knowledge_system/ArticleCreate"));
const ArticleEdit = lazy(() => import("../pages/knowledge_system/ArticleEdit"));
const KnowledgeCategoryList = lazy(() => import("../pages/knowledge_system/KnowledgeCategoryList"));
const KnowledgeTagList = lazy(() => import("../pages/knowledge_system/KnowledgeTagList"));

// Others
const AppStore = lazy(() => import("../pages/apps/AppStore"));
const Inbox = lazy(() => import("../pages/inbox/Inbox"));
const InboxDashboard = lazy(() => import("../pages/inbox/components/InboxDashboard"));
const WhatsAppTemplates = lazy(() => import("../pages/inbox/components/WhatsAppTemplates"));
const ChatHistory = lazy(() => import("../pages/inbox/components/ChatHistory"));
const ChatbotList = lazy(() => import("../pages/inbox/components/ChatbotList"));
const ChatbotBuilder = lazy(() => import("../pages/inbox/components/ChatbotBuilder"));
const ChannelsConfig = lazy(() => import("../pages/inbox/components/ChannelsConfig"));
const ChatWidgetTest = lazy(() => import("../pages/widget/ChatWidgetTest"));
const PublicSurvey = lazy(() => import("../pages/customer_satisfaction/PublicSurvey"));

// CSAT
const CSATDashboard = lazy(() => import("../pages/customer_satisfaction/CSATDashboard"));
const SurveyList = lazy(() => import("../pages/customer_satisfaction/SurveyList"));
const SurveyCreate = lazy(() => import("../pages/customer_satisfaction/SurveyCreate"));
const SurveyResponses = lazy(() => import("../pages/customer_satisfaction/SurveyResponses"));
const CSATReports = lazy(() => import("../pages/customer_satisfaction/CSATReports"));

// NEXUS — Agentic AI Studio
const NexusDashboard = lazy(() => import("../pages/nexus/NexusDashboard"));
const NexusAgentsList = lazy(() => import("../pages/nexus/AgentsList"));
const NexusSkillsList = lazy(() => import("../pages/nexus/SkillsList"));
const NexusRunDetail = lazy(() => import("../pages/nexus/RunDetail"));
const NexusTeamsList = lazy(() => import("../pages/nexus/TeamsList"));
const NexusTools = lazy(() => import("../pages/nexus/ToolsManager"));
const NexusApprovals = lazy(() => import("../pages/nexus/ApprovalInbox"));
const NexusVault = lazy(() => import("../pages/nexus/CredentialVault"));
const NexusMCP = lazy(() => import("../pages/nexus/MCPConfig"));
const NexusObservability = lazy(() => import("../pages/nexus/Observability"));
const NexusBenchmark = lazy(() => import("../pages/nexus/Benchmark"));
const NexusSettings = lazy(() => import("../pages/nexus/NexusSettings"));

// CRM
const CRM = lazy(() => import("../pages/partner/CRM"));
const LeadCreate = lazy(() => import("../pages/partner/LeadCreate"));
const LeadDashboard = lazy(() => import("../pages/partner/LeadDashboard"));
const CRMDashboard = lazy(() => import("../pages/partner/CRMDashboard"));
const LeadList = lazy(() => import("../pages/partner/LeadList"));
const OpportunityBoard = lazy(() => import("../pages/partner/opportunities/OpportunityBoard"));
const OpportunityCreate = lazy(() => import("../pages/partner/opportunities/OpportunityCreate"));
const OpportunityDetail = lazy(() => import("../pages/partner/opportunities/OpportunityDetail"));
const AccountDetail = lazy(() => import("../pages/partner/AccountDetail"));
const AccountEdit = lazy(() => import("../pages/partner/AccountEdit"));
const ProposalPreviewPage = lazy(() => import("../pages/partner/opportunities/ProposalPreviewPage"));
const Forecasting = lazy(() => import("../pages/partner/Forecasting"));
const CRMTasks = lazy(() => import("../pages/partner/tasks/CRMTasks"));
const CRMReports = lazy(() => import("../pages/partner/CRMReports"));
const WebToLeadForm = lazy(() => import("../pages/partner/WebToLeadForm"));
const AssignmentRules = lazy(() => import("../pages/partner/AssignmentRules"));
const EmailSequences = lazy(() => import("../pages/partner/EmailSequences"));
const RevenueIntelligence = lazy(() => import("../pages/partner/RevenueIntelligence"));
const MRRDashboard = lazy(() => import("../pages/partner/MRRDashboard"));
const ContractsList = lazy(() => import("../pages/partner/ContractsList"));
const MeetingScheduler = lazy(() => import("../pages/partner/MeetingScheduler"));

// E-Sign (public — no auth)
const PublicSignPage = lazy(() => import("../pages/esign/PublicSignPage"));

// Meeting Scheduler (public — no auth)
const PublicBookingPage = lazy(() => import("../pages/scheduling/PublicBookingPage"));
const PublicCancelPage = lazy(() => import("../pages/scheduling/PublicBookingPage").then(m => ({ default: m.PublicCancelPage })));

// Customer Portal (public — portal-token auth, not Django auth)
const CustomerPortal = lazy(() => import("../pages/portal/CustomerPortal"));

// Integration Hub
const IntegrationHub = lazy(() => import("../pages/integrations/IntegrationHub"));

// Projects
const ProjectsModuleDashboard = lazy(() => import("../pages/projects/ProjectsModuleDashboard"));
const AllProjectsOverview = lazy(() => import("../pages/projects/AllProjectsOverview"));
const MyTasksView = lazy(() => import("../pages/projects/MyTasksView"));
const ProjectsList = lazy(() => import("../pages/projects/ProjectsList"));
const ProjectOverview = lazy(() => import("../pages/projects/ProjectOverview"));
const ProjectBoard = lazy(() => import("../pages/projects/ProjectBoard"));
const TaskDetailPage = lazy(() => import("../pages/projects/TaskDetailPage"));
const ProjectListView = lazy(() => import("../pages/projects/ProjectListView"));
const ProjectGantt = lazy(() => import("../pages/projects/ProjectGantt"));
const ProjectMilestones = lazy(() => import("../pages/projects/ProjectMilestones"));
const ProjectTimesheets = lazy(() => import("../pages/projects/ProjectTimesheets"));
const TimesheetApprovalDashboard = lazy(() => import("../pages/projects/TimesheetApprovalDashboard"));
const WorkloadView = lazy(() => import("../pages/projects/WorkloadView"));
const ProjectSprints = lazy(() => import("../pages/projects/ProjectSprints"));
const ProjectTemplates = lazy(() => import("../pages/projects/ProjectTemplates"));
const ProjectSettings = lazy(() => import("../pages/projects/ProjectSettings"));
const ProjectReports = lazy(() => import("../pages/projects/ProjectReports"));

// Huddle — Collaboration
const HuddlePage = lazy(() => import("../pages/huddle/Huddle"));

// Calendar
const CalendarApp = lazy(() => import("../pages/calendar/CalendarApp"));
const PublicBookingApp = lazy(() => import("../pages/calendar/public/PublicBookingApp"));
const CalendarIntegrations = lazy(() => import("../pages/calendar/settings/CalendarIntegrations"));
const IntegrationCallback = lazy(() => import("../pages/calendar/settings/IntegrationCallback"));
const AppointmentsDashboard = lazy(() => import("../pages/appointments/AppointmentsDashboard"));
const ServiceBooking = lazy(() => import("../pages/appointments/ServiceBooking"));

// CPQ
const ProductCreate = lazy(() => import("../pages/partner/products/ProductCreate"));
const ProductDetail = lazy(() => import("../pages/partner/products/ProductDetail"));

// Helpdesk - SLA
const SLAList = lazy(() => import("../pages/helpdesk/SLAList"));
const SLADetails = lazy(() => import("../pages/helpdesk/SLADetailsLayout/SLADetails"));
const SLAEdit = lazy(() => import("../pages/helpdesk/SLAEdit"));

// Helpdesk - Category
const CategoryList = lazy(() => import("../pages/helpdesk/Category/CategoryList"));
const CategoryCreate = lazy(() => import("../pages/helpdesk/Category/CategoryCreate"));
const CategoryDetails = lazy(() => import("../pages/helpdesk/Category/CategoryDetailsLayout/CategoryDetails"));
const CategoryEdit = lazy(() => import("../pages/helpdesk/Category/CategoryEdit"));

// Helpdesk - Team
const TeamList = lazy(() => import("../pages/helpdesk/Team/TeamList"));
const TeamCreate = lazy(() => import("../pages/helpdesk/Team/TeamCreate"));
const TeamDetails = lazy(() => import("../pages/helpdesk/Team/TeamDetailsLayout/TeamDetails"));
const TeamEdit = lazy(() => import("../pages/helpdesk/Team/TeamEdit"));

// Search
const GlobalSearchResults = lazy(() => import("../pages/search/GlobalSearchResults"));
const Unauthorized = lazy(() => import("../pages/Unauthorized"));

// Where "/" lands for a signed-in person: the AI home when Nexus is
// installed for the tenant, otherwise the dashboard.
const HomeIndex = () => {
  const { isAppInstalled, isAppsLoading } = useInstalledApps();
  if (isAppsLoading) return null;
  return <Navigate to={isAppInstalled('nexus') ? "/home" : "/dashboard"} replace />;
};

const Routes = () => {
  const { token } = useAuth();

  // Public Routes (Accessible by anyone)
  const publicRoutes = [
    {
      path: "/test-widget/:botId",
      element: <ChatWidgetTest />
    },
    {
      path: "/s/:uuid",
      element: <PublicSurvey />
    },
    {
      path: "/proposals/preview/:quoteId",
      element: <ProposalPreviewPage />
    },
    {
      path: "/lead-form",
      element: <WebToLeadForm />
    },
    // ── Public E-Sign ── no auth required
    {
      path: "/sign/:token",
      element: <PublicSignPage />
    },
    // ── Public Meeting Booking ── no auth required
    {
      path: "/schedule/:slug",
      element: <PublicBookingPage />
    },
    {
      path: "/book/:slug",
      element: <PublicBookingApp />
    },
    {
      path: "/schedule/cancel/:cancel_token",
      element: <PublicCancelPage />
    },
    // ── Customer Self-Service Portal ── uses portal-token auth
    {
      path: "/portal/:slug",
      element: <CustomerPortal />
    },
    {
      path: "/portal/:slug/verify",
      element: <CustomerPortal />
    },
  ];

  const routesForAuthenticatedOnly = [
    {
      path: "/",
      // The chat overlay lives inside the router so it can step aside on /home.
      element: <><RegistryInitializer /><ProtectedRoute /><AIChatOverlay /></>,
      children: [
        {
          index: true,
          element: <HomeIndex />,
        },
        {
          element: <ProtectedRoute requiredApp="nexus" />,
          children: [
            { path: "/home", element: <AIHome /> },
            { path: "/home/c/:conversationId", element: <AIHome /> },
          ],
        },
        {
          path: "/dashboard",
          element: <Dashboard />,
        },
        {
          path: "/profile",
          element: <Profile />,
        },
        {
          path: "/unauthorized",
          element: <Unauthorized />,
        },
        // ──────────────────────────────────────────────────────────
        // ADMINISTRATION / USERS & SETTINGS
        // ──────────────────────────────────────────────────────────
        {
          element: <ProtectedRoute allowedGroups={['Administration / Settings', 'Administration / Access Rights']} />,
          children: [
            {
              path: "/users/list",
              element: <UsersList />,
            },
            {
              path: "/users",
              element: <UsersGrid />,
            },
            {
              path: "/users/create",
              element: <UserCreate />,
            },
            {
              path: "/users/:id",
              element: <UserDetails />,
            },
            {
              path: "/users/:id/edit",
              element: <UserEdit />,
            },
            {
              path: "settings",
              element: <Settings />,
            },
            {
              path: "settings/edit",
              element: <SettingsEdit />,
            },
            {
              path: "/company/create",
              element: <CompanyCreate />,
            },
            {
              path: "/company",
              element: <CompanyGrid />,
            },
            {
              path: "/company/list",
              element: <CompanyList />,
            },
            {
              path: "/company/:id",
              element: <CompanyDetails />,
            },
            {
              path: "/company/:id/edit",
              element: <CompanyEdit />,
            },
            {
              path: "settings/translations",
              element: <TranslationsSettings />,
            },
            {
              path: "settings/ai",
              element: <AISettings />,
            },
            {
              path: "settings/gateways",
              element: <GatewaySettings />,
            },
            {
              path: "settings/resource-calendars",
              element: <ResourceCalendarList />,
            },
            {
              path: "settings/resource-calendars/create",
              element: <ResourceCalendarDetails />,
            },
            {
              path: "settings/resource-calendars/:id",
              element: <ResourceCalendarDetails />,
            },
            {
              path: "/apps",
              element: <AppStore />,
            },
            {
              path: "/saas-admin",
              element: <SaaSAdmin />,
            },
          ]
        },

        // ──────────────────────────────────────────────────────────
        // EMAIL SERVICES
        // ──────────────────────────────────────────────────────────
        {
          element: <ProtectedRoute allowedGroups={['Email / Manager', 'Administration / Settings', 'Administration / Access Rights']} />,
          children: [
            {
              path: "settings/emails",
              element: <EmailConfiguration />,
            },
            {
              path: "settings/emails/create",
              element: <EmailConfiguration />,
            },
            {
              path: "settings/emails/:id",
              element: <EmailConfiguration />,
            },
            {
              path: "settings/emails/:id/edit",
              element: <EmailConfiguration />,
            },
            {
              path: "/email-logs",
              element: <EmailLogs />,
            },
          ]
        },

        // ──────────────────────────────────────────────────────────
        // INTEGRATION HUB
        // ──────────────────────────────────────────────────────────
        {
          element: <ProtectedRoute allowedGroups={['Administration / Settings', 'Administration / Access Rights']} />,
          children: [
            {
              path: "/integrations",
              element: <IntegrationHub />,
            },
          ]
        },

        // ──────────────────────────────────────────────────────────
        // PROJECTS MODULE
        // ──────────────────────────────────────────────────────────
        {
          element: <ProtectedRoute />,
          children: [
            {
              path: "/projects",
              element: <AllProjectsOverview />,
            },
            {
              path: "/projects/overview",
              element: <AllProjectsOverview />,
            },
            {
              path: "/projects/dashboard",
              element: <ProjectsModuleDashboard />,
            },
            {
              path: "/projects/list",
              element: <ProjectsList />,
            },
            {
              path: "/projects/my-tasks",
              element: <MyTasksView />,
            },
            {
              path: "/projects/:id/overview",
              element: <ProjectOverview />,
            },
            {
              path: "/projects/:id/board",
              element: <ProjectBoard />,
            },
            {
              path: "/projects/:id/list",
              element: <ProjectListView />,
            },
            {
              path: "/projects/:id/tasks/:taskId",
              element: <TaskDetailPage />,
            },
            {
              path: "/projects/:id/gantt",
              element: <ProjectGantt />,
            },
            {
              path: "/projects/:id/milestones",
              element: <ProjectMilestones />,
            },
            {
              path: "/projects/:id/timesheets",
              element: <ProjectTimesheets />,
            },
            {
              path: "/projects/:id/sprints",
              element: <ProjectSprints />,
            },
            {
              path: "/projects/:id/reports",
              element: <ProjectReports />,
            },
            {
              // The page itself decides what each project role may change.
              path: "/projects/:id/settings",
              element: <ProjectSettings />,
            },
            {
              // Manager pages, matching modules/projects/api/permissions.py.
              // A normal user's own entries live on /projects/:id/timesheets.
              element: <ProtectedRoute allowedGroups={['Project / Manager', 'Administration / Settings']} />,
              children: [
                {
                  path: "/projects/templates",
                  element: <ProjectTemplates />,
                },
                {
                  path: "/projects/timesheets",
                  element: <TimesheetApprovalDashboard />,
                },
                {
                  path: "/projects/workload",
                  element: <WorkloadView />,
                },
              ]
            },
          ]
        },

        // ──────────────────────────────────────────────────────────
        // HUDDLE — COLLABORATION
        // ──────────────────────────────────────────────────────────
        {
          path: "/huddle",
          element: <HuddlePage />,
        },

        // ──────────────────────────────────────────────────────────
        // CALENDAR MODULE
        // ──────────────────────────────────────────────────────────
        {
          path: "/calendar/*",
          element: <CalendarApp />,
        },
        {
          path: "/calendar/settings/integrations",
          element: <CalendarIntegrations />
        },
        {
          path: "/calendar/integrations/callback",
          element: <IntegrationCallback />
        },
        {
          path: "/appointments",
          element: <AppointmentsDashboard />
        },
        {
          path: "/book/service/:id",
          element: <ServiceBooking />
        },

        // ──────────────────────────────────────────────────────────
        // GLOBAL SEARCH & LOGOUT
        // ──────────────────────────────────────────────────────────
        {
          path: "/search",
          element: <GlobalSearchResults />,
        },
        {
          path: "/logout",
          element: <Logout />,
        },
      ],
    },
  ];

  const routesForNotAuthenticatedOnly = [
    {
      path: "/",
      element: <Homepage />
    },
    {
      path: "/login",
      element: <Login />,
    },
    {
      path: "/register",
      element: <Register />,
    },
  ];

  const router = createBrowserRouter([
    ...publicRoutes,
    ...(!token ? routesForNotAuthenticatedOnly : []),
    ...(token ? routesForAuthenticatedOnly : []),
    {
      path: "*",
      element: <Navigate to="/" replace />,
    }
  ]);

  return (
    <Suspense fallback={<div className="flex items-center justify-center h-screen">Loading...</div>}>
      <RouterProvider router={router} />
    </Suspense>
  );
};

export default Routes;